import {isEmpty} from 'lodash';
import {stringify} from 'query-string';
import Env from './env';
import {ERRORS, HTTP_REQUEST_METHODS} from './uiconstants';

const arrayFormat = 'bracket-separator';
const arrayFormatSeparator = '|';

export const API_RESOURCES = {
	// Add the corresponding available Api resources here
	ENGAGEMENTS: 'engagements',
	FEATURE_CONFIGURATIONS: 'featureConfigurations',
	PROJECTS: {
		PROJECTS: 'projects',
		HEADER: 'header',
		SUMMARY: 'summary',
		FINANCIAL: 'financial',
		BILLING: 'billing',
		FINANCIAL_DETAILS: 'FinancialDetails',
		FINANCIAL_FILTERS: 'FinancialFilter',
		BILLING_DETAILS: 'billingDetails',
		FINANCIAL_EXPENSE_DETAILS: 'financialExpenseDetail',
		EXPENSE_FILTERS: 'ExpenseFilter',
		INDEPENDENCES: 'Independences',
		ENGAGEMENTS: 'Engagements',
		INDEPENDENCEUSERS: 'IndependenceUsers',
		ENGAGEMENTUSERS: 'EngagementUsers'
	},
	USERS: 'users',
	ADMIN: {
		ADMINS: 'admins',
		PROJECTS: 'projects',
		USERS: 'users'
	}
};

/**
 * Builds an Api request Url based on the specified parameters.
 * @param {object} resource An object holding the Url's resource part information.
 * @param {object} queryString An object holding the Url's query string parameters.
 * @returns {string} The Url built using the specified object parameters.
 */
export function getUrl(resource, queryString = undefined) {
	let url = Env.getServerUrl();

	if (resource !== undefined && !isEmpty(resource)) {
		url += Object.entries(resource)
			.map((item) => {
				let path = item[0];
				if (item[1] !== undefined) {
					path += `/${item[1]}`;
				}
				return path;
			})
			.join('/');
	} else {
		throw ERRORS.NO_RESOURCE_SPECIFIED;
	}

	let _queryString;
	if (queryString) {
		_queryString = stringify(queryString, {
			arrayFormat,
			arrayFormatSeparator,
			skipNull: true
		});
		url += `?${_queryString}`;
	}

	return url;
}

/**
 * Creates an action with the specified parameters that will be handled by the api-middleware asynchronously.
 * @param {string} type The type of the created action.
 * @param {object} resource An object holding the Url's resource part information.
 * @param {object} data An optional payload to send to the server if necessary.
 * @param {object} args An optional object with arguments to be available in the action object.
 * @param {boolean} isOnline Specifies whether the application has Api connectivity, defaults to true.
 * @returns {object} The new action object.
 */
export function createApiCreateAction(type, resource, data, args = {}, isOnline = true) {
	return {
		type,
		...args,
		meta: {
			api: {
				url: getUrl(resource),
				method: HTTP_REQUEST_METHODS.POST,
				data
			}
		},
		isOnline
	};
}

/**
 * Creates an action with the specified parameters that will be handled by the api-middleware asynchronously.
 * @param {string} type The type of the created action.
 * @param {object} resource An object holding the Url's resource part information.
 * @param {object} queryString An object holding the Url's query string parameters.
 * @param {object} args An optional object with arguments to be available in the action object.
 * @param {boolean} isOnline Specifies whether the application has Api connectivity, defaults to true.
 * @returns {object} The new action object.
 */
export function createApiReadAction(type, resource, queryString = undefined, args = {}, isOnline = true) {
	return {
		type,
		...args,
		meta: {
			api: {
				url: getUrl(resource, queryString),
				method: HTTP_REQUEST_METHODS.GET
			}
		},
		isOnline
	};
}

/**
 * Creates an action with the specified parameters that will be handled by the api-middleware asynchronously.
 * @param {string} type The type of the created action.
 * @param {object} resource An object holding the Url's resource part information.
 * @param {object} data An optional payload to send to the server if necessary.
 * @param {object} method The Http method to be used, defaults to PUT.
 * @param {object} args An optional object with arguments to be available in the action object.
 * @param {boolean} isOnline Specifies whether the application has Api connectivity, defaults to true.
 * @returns {object} The new action object.
 */
export function createApiUpdateAction(type, resource, data = undefined, method = HTTP_REQUEST_METHODS.PUT, args = {}, isOnline = true) {
	return {
		type,
		...args,
		meta: {
			api: {
				url: getUrl(resource),
				method,
				data
			}
		},
		isOnline
	};
}

/**
 * Creates an action with the specified parameters that will be handled by the api-middleware asynchronously.
 * @param {string} type The type of the created action.
 * @param {object} resource An object holding the Url's resource part information.
 * @param {object} args An optional object with arguments to be available in the action object.
 * @param {boolean} isOnline Specifies whether the application has Api connectivity, defaults to true.
 * @returns {object} The new action object.
 */
export function createApiDeleteAction(type, resource, args = {}, isOnline = true) {
	return {
		type,
		...args,
		meta: {
			api: {
				url: getUrl(resource),
				method: HTTP_REQUEST_METHODS.DELETE
			}
		},
		isOnline
	};
}

/**
 * patch an action with the specified parameters that will be handled by the api-middleware asynchronously.
 * @param {string} type The type of the created action.
 * @param {object} resource An object holding the Url's resource part information.
 * @param {object} data An optional payload to send to the server if necessary.
 * @param {object} method The Http method to be used, defaults to PATCH.
 * @param {object} args An optional object with arguments to be available in the action object.
 * @param {boolean} isOnline Specifies whether the application has Api connectivity, defaults to true.
 * @returns {object} The new action object.
 */
export function patchApiUpdateAction(type, resource, data = undefined, method = HTTP_REQUEST_METHODS.PATCH, args = {}, isOnline = true) {
	return {
		type,
		...args,
		meta: {
			api: {
				url: getUrl(resource),
				method,
				data
			}
		},
		isOnline
	};
}
